import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../app/store";
import { ChatMessageType } from "../contracts";
import { getLastAgentResponse, isCommercialSimulatorAgentResponse, isCommercialSimulatorSubBrandAgentResponse, type CommercialSimulatorAgentResponse } from "../models/agentResponseModels";
import { type ChatConversationViewModel, type ChatMessageViewModel } from "../models/chatMessageViewModel";
import { compareByNullableNumber } from "../utils/sortUtils";

export interface CommericalPlanState {
    currentCommercialPlan: CommercialSimulatorAgentResponse | null | undefined;
    currentCommercialPlanMessageId: number | null | undefined;
}

export type SimulationSetupSelection = {
    retailer?: string | null;
    planningLevel?: string | null;
    selection?: string | null;
    timeframe?: string | null;
    goal?: string | null;
    budget?: string | null;
    canGenerate?: boolean;
};

export interface ChatMessagesState {
    conversation: ChatConversationViewModel | null | undefined;
    currentCommercialPlan: CommercialSimulatorAgentResponse | null | undefined;
    currentCommercialPlanMessageId: number | null | undefined;
    simulationSetupSelection: SimulationSetupSelection;
    generateSimulationRequested: boolean;
}

const emptySimulationSetupSelection: SimulationSetupSelection = {};

const initialState: ChatMessagesState = {
    conversation: null,
    currentCommercialPlan: null,
    currentCommercialPlanMessageId: null,
    simulationSetupSelection: emptySimulationSetupSelection,
    generateSimulationRequested: false
};

export const selectConversation = (state: RootState) => state.chat.conversation;
export const selectMessages = (state: RootState) => state.chat.conversation?.messages;
export const selectCurrentCommercialPlan = (state: RootState) => state.chat.currentCommercialPlan;
export const selectCurrentCommercialPlanMessageId = (state: RootState) => state.chat.currentCommercialPlanMessageId;
export const selectSimulationSetupSelection = (state: RootState) => state.chat.simulationSetupSelection;
export const selectGenerateSimulationRequested = (state: RootState) => state.chat.generateSimulationRequested;

const chatSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        setCurrentCommercialPlan: (state, action: PayloadAction<CommericalPlanState | null | undefined>) => {
            state.currentCommercialPlan = action.payload?.currentCommercialPlan;
            state.currentCommercialPlanMessageId = action.payload?.currentCommercialPlanMessageId;
        },
        clearCurrentCommercialPlan: state => {
            state.currentCommercialPlan = null;
            state.currentCommercialPlanMessageId = null;
        },
        setSimulationSetupSelection: (state, action: PayloadAction<SimulationSetupSelection>) => {
            state.simulationSetupSelection = {
                ...state.simulationSetupSelection,
                ...action.payload
            };
        },
        clearSimulationSetupSelection: state => {
            state.simulationSetupSelection = emptySimulationSetupSelection;
            state.generateSimulationRequested = false;
        },
        requestGenerateSimulation: state => {
            state.generateSimulationRequested = true;
        },
        clearGenerateSimulationRequest: state => {
            state.generateSimulationRequested = false;
        },
        setConversation: (state, action: PayloadAction<ChatConversationViewModel | null | undefined>) => {
            state.conversation = action.payload;
            state.currentCommercialPlan = null;
            state.currentCommercialPlanMessageId = null;
            state.simulationSetupSelection = emptySimulationSetupSelection;
            state.generateSimulationRequested = false;

            const agentResponse = getLastAgentResponse(action.payload?.messages);
            if (agentResponse && (isCommercialSimulatorAgentResponse(agentResponse.type, agentResponse.action, agentResponse.payload)
                || isCommercialSimulatorSubBrandAgentResponse(agentResponse.type, agentResponse.action, agentResponse.payload))) {
                state.currentCommercialPlan = agentResponse.payload;
                state.currentCommercialPlanMessageId = agentResponse.id;
            }
        },
        clearConversation: state => {
            state.conversation = null;
            state.simulationSetupSelection = emptySimulationSetupSelection;
            state.generateSimulationRequested = false;
        },
        setConversationBusy: (state, action: PayloadAction<{ is_busy: boolean }>) => {
            if (!state.conversation) {
                return;
            }

            state.conversation = {
                ...state.conversation,
                is_busy: action.payload.is_busy
            };
        },
        setMessages: (state, action: PayloadAction<ChatMessageViewModel[]>) => {
            if (!state.conversation) {
                return;
            }

            state.conversation.messages = [...action.payload].sort(
                compareByNullableNumber(message => message.sequence)
            );
        },
        clearMessages: state => {
            if (!state.conversation) {
                return;
            }

            state.conversation.messages = [];
        },
        appendMessage: (state, action: PayloadAction<ChatMessageViewModel>) => {
            const conversation = state.conversation;
            if (!conversation) {
                return;
            }

            const message = action.payload;

            const messageIndex = conversation.messages.findIndex(existingMessage =>
                (message.id != null && existingMessage.id === message.id)
                || (message.client_message_id != null && existingMessage.client_message_id === message.client_message_id)
            );

            const messages = messageIndex === -1
                ? [...conversation.messages, message]
                : conversation.messages.map((existingMessage, index) =>
                        index === messageIndex
                            ? message
                            : existingMessage
                    );

            state.conversation = {
                ...conversation,
                messages: messages.sort(compareByNullableNumber(message => message.sequence))
            };
        },
        removeMessageByClientId: (state, action: PayloadAction<string>) => {
            if (!state.conversation) {
                return;
            }
            state.conversation.messages = state.conversation.messages.filter(message => message.client_message_id !== action.payload);
        },
        removePendingToolRequest: state => {
            if (!state.conversation) {
                return;
            }
            const currentLastMessage = state.conversation.messages.length > 0 ? state.conversation.messages[state.conversation.messages.length - 1] : undefined;
            if (currentLastMessage?.type !== ChatMessageType.ToolRequest) {
                return;
            }

            const previousMessage = state.conversation.messages.length > 1 ? state.conversation.messages[state.conversation.messages.length - 2] : undefined;
            state.conversation.messages = previousMessage?.type === ChatMessageType.User
                ? state.conversation.messages.slice(0, -2)
                : state.conversation.messages.slice(0, -1);
            state.simulationSetupSelection = emptySimulationSetupSelection;
            state.generateSimulationRequested = false;
        }
    }
});

export const {
    setConversation,
    clearConversation,
    setConversationBusy,
    setCurrentCommercialPlan,
    clearCurrentCommercialPlan,
    setSimulationSetupSelection,
    clearSimulationSetupSelection,
    requestGenerateSimulation,
    clearGenerateSimulationRequest,
    setMessages,
    clearMessages,
    appendMessage,
    removeMessageByClientId,
    removePendingToolRequest
} = chatSlice.actions;

export default chatSlice.reducer;
